import React from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Activity, 
  Zap,
  Clock,
  AlertCircle,
  Key,
  RefreshCw
} from 'lucide-react';

interface UsageData {
  period: string;
  requests: number;
  limit: number;
}

const mockUsageData: UsageData[] = [
  { period: '00:00', requests: 45, limit: 100 },
  { period: '04:00', requests: 32, limit: 100 },
  { period: '08:00', requests: 78, limit: 100 },
  { period: '12:00', requests: 92, limit: 100 },
  { period: '16:00', requests: 67, limit: 100 },
  { period: '20:00', requests: 54, limit: 100 }
];

export const UsageAnalytics: React.FC = () => {
  const currentUsage = mockUsageData[mockUsageData.length - 1];
  const usagePercentage = (currentUsage.requests / currentUsage.limit) * 100;
  const totalRequests = 1247;
  const remainingRequests = 100 - currentUsage.requests;

  return (
    <div className="space-y-6">
      {/* API Key Management */}
      <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <div className="p-1 bg-yellow-500/20 rounded-lg">
              <Key className="w-4 h-4 text-yellow-400" />
            </div>
            API Access
          </h3>
          <button className="flex items-center gap-1 px-3 py-1 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-xs text-gray-400 transition-colors">
            <RefreshCw className="w-3 h-3" />
            Regenerate
          </button>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <span className="text-gray-300 text-sm">Current Plan</span>
            <span className="text-emerald-400 font-bold text-sm">Free Tier</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <span className="text-gray-300 text-sm">API Key</span>
            <code className="text-xs text-gray-400 font-mono">pk_***...***abc</code>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <span className="text-gray-300 text-sm">Rate Limit</span>
            <span className="text-blue-400 font-bold text-sm">100/day</span>
          </div>
        </div>
      </div>

      {/* Usage Overview */}
      <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <div className="p-1 bg-blue-500/20 rounded-lg">
            <BarChart3 className="w-4 h-4 text-blue-400" />
          </div>
          Usage Analytics
        </h3>
        
        {/* Current Usage */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300 text-sm">Today's Usage</span>
            <span className="text-white font-bold">{currentUsage.requests}/{currentUsage.limit}</span>
          </div>
          <div className="w-full bg-gray-800/50 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                usagePercentage > 90 ? 'bg-red-500' : 
                usagePercentage > 70 ? 'bg-yellow-500' : 'bg-emerald-500'
              }`}
              data-usage={usagePercentage}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{usagePercentage.toFixed(1)}% used</span>
            <span>{remainingRequests} remaining</span>
          </div>
        </div>

        {/* Usage Chart */}
        <div className="mb-4">
          <div className="flex items-center gap-4 mb-3">
            <span className="text-gray-300 text-sm">24h Activity</span>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-gray-400">API Requests</span>
            </div>
          </div>
          <div className="flex items-end justify-between h-20 gap-1">
            {mockUsageData.map((data, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div 
                  className={`w-full bg-blue-500/30 rounded-t transition-all duration-300 hover:bg-blue-500/50 ${
                    data.requests > 80 ? 'h-20' :
                    data.requests > 60 ? 'h-16' :
                    data.requests > 40 ? 'h-12' :
                    data.requests > 20 ? 'h-8' : 'h-4'
                  }`}
                ></div>
                <span className="text-xs text-gray-500 mt-1">{data.period}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <div className="p-1 bg-emerald-500/20 rounded-lg">
            <Activity className="w-4 h-4 text-emerald-400" />
          </div>
          Performance
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-gray-300 text-sm">Avg Response Time</span>
            </div>
            <span className="text-yellow-400 font-bold text-sm">42ms</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-gray-300 text-sm">Success Rate</span>
            </div>
            <span className="text-emerald-400 font-bold text-sm">99.8%</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300 text-sm">Total Requests</span>
            </div>
            <span className="text-blue-400 font-bold text-sm">{totalRequests.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Alerts & Notifications */}
      <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <div className="p-1 bg-orange-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-orange-400" />
          </div>
          Alerts
        </h3>
        
        <div className="space-y-2">
          {usagePercentage > 80 && (
            <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-yellow-400" />
              <div>
                <div className="text-yellow-300 text-sm font-medium">High Usage Warning</div>
                <div className="text-yellow-200/80 text-xs">You've used {usagePercentage.toFixed(1)}% of your daily limit</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <div>
              <div className="text-emerald-300 text-sm font-medium">All Systems Operational</div>
              <div className="text-emerald-200/80 text-xs">API is running smoothly</div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Prompt */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-blue-400" />
          <h4 className="text-white font-medium">Need More?</h4>
        </div>
        <p className="text-gray-400 text-sm mb-3">
          Upgrade to Pro for 10,000 daily requests and real-time WebSocket feeds.
        </p>
        <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-sm">
          Upgrade to Pro
        </button>
      </div>
    </div>
  );
};
